import React, { useMemo } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import SearchableSelect from '../SearchableSelect';
import { MasterUserAdmin } from '../../types';

export interface TenantUserDraft {
  key: string;
  userId: number | null;
  role: string;
  isDefault: boolean;
}

const ROLE_OPTIONS = ['Admin', 'User', 'Vendedor', 'Supervisor', 'Operador', 'SUPERADMIN'].map(
  (role) => ({ value: role, label: role }),
);

interface TenantUsersSectionProps {
  users: MasterUserAdmin[];
  rows: TenantUserDraft[];
  disabled?: boolean;
  onChange: (rows: TenantUserDraft[]) => void;
}

const createRow = (): TenantUserDraft => ({
  key: `row-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  userId: null,
  role: 'User',
  isDefault: false,
});

const TenantUsersSection: React.FC<TenantUsersSectionProps> = ({
  users,
  rows,
  disabled = false,
  onChange,
}) => {
  const userOptions = useMemo(
    () =>
      users.map((user) => ({
        value: user.id,
        label: user.displayName
          ? `${user.username} — ${user.displayName}`
          : user.username,
      })),
    [users],
  );

  const usersById = useMemo(
    () => new Map(users.map((user) => [user.id, user])),
    [users],
  );

  const usedUserIds = useMemo(
    () => new Set(rows.map((row) => row.userId).filter((id): id is number => id != null)),
    [rows],
  );

  const updateRow = (key: string, patch: Partial<TenantUserDraft>) => {
    onChange(rows.map((row) => (row.key === key ? { ...row, ...patch } : row)));
  };

  const removeRow = (key: string) => {
    onChange(rows.filter((row) => row.key !== key));
  };

  const addRow = () => {
    onChange([...rows, createRow()]);
  };

  const setDefault = (key: string, checked: boolean) => {
    updateRow(key, { isDefault: checked });
  };

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        spacing={1}
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#111827' }}>
            Asignación de usuarios
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Vincula usuarios maestros a este tenant con su rol correspondiente.
          </Typography>
        </Box>
        <Button
          variant="outlined"
          color="primary"
          startIcon={<AddIcon />}
          onClick={addRow}
          disabled={disabled}
          sx={{ textTransform: 'none' }}
        >
          Agregar usuario
        </Button>
      </Stack>

      {rows.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
          Este tenant no tiene usuarios asignados.
        </Typography>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                <TableCell sx={{ fontWeight: 600, minWidth: 240 }}>Usuario</TableCell>
                <TableCell sx={{ fontWeight: 600, minWidth: 160 }}>Rol</TableCell>
                <TableCell sx={{ fontWeight: 600, width: 120 }} align="center">
                  Por defecto
                </TableCell>
                <TableCell sx={{ fontWeight: 600, width: 72 }} align="center">
                  Acciones
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => {
                const optionsForRow = userOptions.filter(
                  (option) => option.value === row.userId || !usedUserIds.has(option.value),
                );
                const selectedUser = row.userId != null ? usersById.get(row.userId) : undefined;

                return (
                  <TableRow key={row.key} hover>
                    <TableCell>
                      <Stack spacing={0.5}>
                        <SearchableSelect
                          label="Usuario"
                          value={row.userId}
                          onChange={(value) => updateRow(row.key, { userId: value })}
                          options={optionsForRow}
                          disabled={disabled}
                          sx={{ minWidth: 220 }}
                        />
                        {selectedUser && !selectedUser.isActive && (
                          <Chip label="Usuario inactivo" size="small" color="warning" variant="outlined" />
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <SearchableSelect
                        label="Rol"
                        value={row.role}
                        onChange={(value) => updateRow(row.key, { role: value ?? 'User' })}
                        options={ROLE_OPTIONS}
                        disabled={disabled}
                        sx={{ minWidth: 140 }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={row.isDefault}
                            onChange={(e) => setDefault(row.key, e.target.checked)}
                            disabled={disabled || row.userId == null}
                          />
                        }
                        label=""
                        sx={{ m: 0 }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => removeRow(row.key)}
                        disabled={disabled}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default TenantUsersSection;

export function mapUsersToDrafts(
  assignments: Array<{
    userId: number;
    role: string;
    isDefault: boolean;
  }>,
): TenantUserDraft[] {
  return assignments.map((assignment) => ({
    key: `user-${assignment.userId}`,
    userId: assignment.userId,
    role: assignment.role || 'User',
    isDefault: assignment.isDefault,
  }));
}

export function draftsToUserInputs(
  rows: TenantUserDraft[],
): Array<{ userId: number; role: string; isDefault: boolean }> {
  return rows
    .filter((row): row is TenantUserDraft & { userId: number } => row.userId != null)
    .map((row) => ({
      userId: row.userId,
      role: row.role.trim() || 'User',
      isDefault: row.isDefault,
    }));
}

export function validateUserDrafts(rows: TenantUserDraft[]): string | null {
  const assigned = draftsToUserInputs(rows);
  const userIds = rows.map((row) => row.userId);
  if (userIds.some((id) => id == null)) {
    return 'Seleccione un usuario en cada fila o elimine las filas incompletas.';
  }
  const unique = new Set(assigned.map((row) => row.userId));
  if (unique.size !== assigned.length) {
    return 'No puede repetir el mismo usuario en más de una fila.';
  }
  return null;
}
